import type { IotData } from 'aws-sdk'
import { DeviceThing } from '../../types/DeviceThing.types'
import { ConfigurationChanges, ShadowDocument, ShadowDocuments, ShadowPatch, ShadowUpdateResult } from '../../types/ShadowDocument.types'
import { Shadow } from '../../types/Shadow.types'
import { formatTimestamp, getLatestTimestamp } from '../../utils/time'
import { effectiveState, encodeValue, mergeConfiguration, normalizeValue, sameConfigurationValue, shadowNames } from './configuration'
import { describeError } from './errors'

// The list reuses shadows until logout or a save of that shadow; the editor always reads fresh ones.
let caches = new WeakMap<IotData, Map<string, Promise<ShadowDocument>>>()
const cacheKey = (thingName: string, shadowName: Shadow) => JSON.stringify([thingName, shadowName])

export function clearShadowsCache() {
    caches = new WeakMap()
}
function invalidateShadow(thingName: string, shadowName: Shadow, iotData: IotData) {
    caches.get(iotData)?.delete(cacheKey(thingName, shadowName))
}

function parseDocument(payload: IotData.JsonDocument | undefined): ShadowDocument {
    const document = JSON.parse(typeof payload === 'string' ? payload : payload?.toString() || '{}')
    // A shadow without any state is read as an empty one instead of breaking the whole machine.
    return { ...document, state: document.state ?? {} }
}

export async function fetchDeviceShadow(thingName: string, shadowName: Shadow, iotData: IotData, fresh = false): Promise<ShadowDocument> {
    let cache = caches.get(iotData)
    if (!cache) {
        cache = new Map()
        caches.set(iotData, cache)
    }
    const key = cacheKey(thingName, shadowName)
    const cached = cache.get(key)
    if (cached && !fresh) return cached

    const request = iotData
        .getThingShadow({ thingName, shadowName })
        .promise()
        .then((data) => parseDocument(data.payload))
    cache.set(key, request)
    try {
        return await request
    } catch (error) {
        if (cache.get(key) === request) cache.delete(key)
        throw error
    }
}

export function applyShadowDocument(device: DeviceThing, name: Shadow, document: ShadowDocument): DeviceThing {
    const effective = effectiveState(document)
    const shadowDocuments = { ...device.shadowDocuments, [name]: document }
    const shadowErrors = { ...device.shadowErrors }
    delete shadowErrors[name]
    const { configuration, fieldReadErrors: currentFieldReadErrors } = mergeConfiguration(name, effective)
    const fieldReadErrors = Object.fromEntries(Object.entries(device.fieldReadErrors).filter(([path]) => !path.startsWith(`${name}.`)))
    Object.assign(fieldReadErrors, currentFieldReadErrors)
    const lastUpdate = Math.max(0, ...Object.values(shadowDocuments).map((doc) => getLatestTimestamp(doc?.metadata?.reported || {})))
    return {
        ...device,
        [name]: configuration,
        instance: name === Shadow.Datasyncd ? String(effective.RESTAPI_HOST || '') : device.instance,
        shadowDocuments,
        shadowErrors,
        fieldReadErrors,
        lastUpdate,
        lastUpdateString: formatTimestamp(lastUpdate),
    }
}

export type ShadowReadResult = { name: Shadow; document: ShadowDocument } | { name: Shadow; error: string }

// Each shadow is read independently: one unavailable section must not hide the others.
export function fetchDeviceShadows(
    thingName: string,
    iotData: IotData,
    shadows: readonly Shadow[] = shadowNames,
    fresh = false
): Promise<ShadowReadResult[]> {
    return Promise.all(
        shadows.map(async (name): Promise<ShadowReadResult> => {
            try {
                return { name, document: await fetchDeviceShadow(thingName, name, iotData, fresh) }
            } catch (error) {
                return { name, error: describeError(error) }
            }
        })
    )
}

export function applyShadowResults(device: DeviceThing, results: ShadowReadResult[]): DeviceThing {
    return results.reduce(
        (result, shadow) =>
            'document' in shadow
                ? applyShadowDocument(result, shadow.name, shadow.document)
                : { ...result, shadowErrors: { ...result.shadowErrors, [shadow.name]: shadow.error } },
        device
    )
}

export async function updateDeviceShadow(
    thingName: string,
    shadowName: Shadow,
    patch: ShadowPatch,
    baseline: ShadowDocument,
    iotData: IotData
): Promise<ShadowUpdateResult> {
    try {
        if (!thingName || !baseline) throw new Error('Najpierw wczytaj konfigurację maszyny.')
        // Re-read before saving: do not merge an edit into a cached or default-filled document.
        const current = await fetchDeviceShadow(thingName, shadowName, iotData, true)
        const previousState = effectiveState(baseline)
        const currentState = effectiveState(current)
        const desired: ShadowPatch = {}
        for (const [key, value] of Object.entries(patch)) {
            const normalized = normalizeValue(shadowName, key, value)
            if (sameConfigurationValue(shadowName, key, currentState[key], normalized)) continue
            if (!sameConfigurationValue(shadowName, key, previousState[key], currentState[key])) {
                throw new Error(`Parametr ${key} został zmieniony przez inne źródło. Otwórz maszynę ponownie przed zapisem.`)
            }
            desired[key] = encodeValue(normalized, currentState[key])
        }
        if (Object.keys(desired).length === 0) return { success: true, document: current }
        // AWS enforces the shadow size limit itself (RequestEntityTooLargeException, see describeError).
        const response = await iotData
            .updateThingShadow({
                thingName,
                shadowName,
                payload: JSON.stringify({ state: { desired }, version: current.version }),
            })
            .promise()
        // The response holds only the patch and its own metadata; keep the reported state and its timestamps.
        const { version } = parseDocument(response.payload)
        const state = { desired: { ...current.state.desired, ...desired }, reported: current.state.reported }
        return { success: true, document: { ...current, version, state } }
    } catch (error) {
        return { success: false, message: describeError(error) }
    } finally {
        // Also invalidate after uncertain network failures: AWS may already have accepted the update.
        invalidateShadow(thingName, shadowName, iotData)
    }
}

export async function updateShadowsFromFormState(
    thingName: string,
    changes: ConfigurationChanges,
    documents: ShadowDocuments,
    iotData: IotData
): Promise<Partial<Record<Shadow, ShadowUpdateResult>>> {
    // Each shadow is a separate versioned document, so they are saved independently and in parallel.
    const results = await Promise.all(
        shadowNames
            .filter((shadowName) => Object.keys(changes[shadowName] ?? {}).length > 0)
            .map(async (shadowName): Promise<[Shadow, ShadowUpdateResult]> => {
                const baseline = documents[shadowName]
                const result: ShadowUpdateResult = baseline
                    ? await updateDeviceShadow(thingName, shadowName, changes[shadowName]!, baseline, iotData)
                    : { success: false, message: 'Nie można zapisać konfiguracji, której nie udało się odczytać.' }
                return [shadowName, result]
            })
    )
    return Object.fromEntries(results)
}
